import React, { useState } from "react";
import { Button } from "react-bootstrap";
import CollapsibleSection from "./CollapsibleSection";
import ExpandableTxTable, { TxColumn } from "./ExpandableTxTable";
import { fmtAmount } from "./format";
import ReverseDiscountModal from "./ReverseDiscountModal";
import { useAccessControl } from "../../../access-control/AccessControlContext";

interface DiscountLogSectionProps {
  /** Grouped-by-receipt transactions from plan-and-payments (paymentTransactions) */
  transactions: any[];
  loading: boolean;
  enrollmentId: number;
  onRefresh: () => void;
  /** Config-driven perm (resource 202 jsonConfig). undefined → fall back to ACL. */
  reverseDiscountPerm?: boolean;
}

const DiscountLogSection: React.FC<DiscountLogSectionProps> = ({
  transactions,
  loading,
  enrollmentId,
  onRefresh,
  reverseDiscountPerm,
}) => {
  // Discount Log = type-6 discount transactions (grouped, one row per discount),
  // excluding reversed ones — mirrors the Java Discount Log block.
  const discountRows = transactions.filter(
    (t: any) => t.paymentType === 6 && !t.isReversed,
  );

  const [reverseTarget, setReverseTarget] = useState<any>(null);

  const { canSeeElement } = useAccessControl();
  const canReverseDiscount =
    reverseDiscountPerm !== undefined
      ? reverseDiscountPerm
      : canSeeElement("enrollment-view", "action.reverseDiscount");

  if (loading) {
    return (
      <CollapsibleSection title="Discount Log" defaultOpen={false}>
        <div className="ev-loading">
          <i className="fa fa-spinner fa-spin" /> Loading...
        </div>
      </CollapsibleSection>
    );
  }

  if (discountRows.length === 0) {
    return (
      <CollapsibleSection title="Discount Log" count={0} defaultOpen={false}>
        <div className="ev-empty">No discounts applied</div>
      </CollapsibleSection>
    );
  }

  const columns: TxColumn[] = [
    { header: "Date", cell: (d) => d.paymentDate, nowrap: true },
    // Discount category (e.g. "LUMPSUM", "FEE DISCOUNT - AFT GST") — the real
    // type carried on the transaction, not a hardcoded placeholder.
    { header: "Type", cell: (d) => d.discountCategory || "—" },
    {
      header: "Amount (Before GST)",
      align: "right",
      cell: (d) => fmtAmount(d.amount),
    },
    {
      header: "Action",
      width: 80,
      cell: (d) =>
        canReverseDiscount ? (
          <Button
            variant="secondary"
            size="sm"
            style={{ fontSize: 12, padding: "1px 7px" }}
            onClick={(e) => {
              e.stopPropagation();
              setReverseTarget(d);
            }}
            title="Reverse Discount"
          >
            <i className="fa fa-undo" />
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <CollapsibleSection
        title="Discount Log"
        count={discountRows.length}
        defaultOpen={false}
      >
        <ExpandableTxTable rows={discountRows} columns={columns} />
      </CollapsibleSection>

      {reverseTarget && (
        <ReverseDiscountModal
          show={!!reverseTarget}
          onHide={() => setReverseTarget(null)}
          enrollmentId={enrollmentId}
          discount={reverseTarget}
          onSuccess={() => {
            setReverseTarget(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default DiscountLogSection;
